import { Injectable } from '@angular/core';
import { MatDateFormats } from '@angular/material/core';
import moment from 'moment';
import { hasValue } from '../functions/js-linq';
import { AccessRights } from '../models/AccessRights.model';

const isoDateTimeFormat = 'YYYY-MM-DD[T]HH:mm:ss';
/**
 * CurrentUser is an singleton
 */
@Injectable()
export class CurrentUser {
  public dateFormats: MatDateFormats = {
    parse: {
      dateInput: null
    },
    display: {
      dateInput: null,
      monthYearLabel: 'MMMM Y',
      dateA11yLabel: 'LL',
      monthYearA11yLabel: 'MMMM Y'
    }
  };
  public id: number;
  public userName: string;
  public domain: string;
  public isDefaultTenant: boolean;
  public userType: string;
  public userInitials: string;
  public hiddenMenuItems: Array<string>;
  public securityKey: string;
  public dictionaryISOCode: string;
  public identityHash: string; // unique, safe, user identifier
  public isInit: boolean;
  public isResponseDesignOn: boolean;
  public accessRights: AccessRights;
  public email: string;
  public isEuAccessCamUser: boolean;


  /** Returns user date time format.
   Example: If the user's date format is YYYY-MM-DD then it returns: YYYY-MM-DD hh:mm:ss A
  */
  public getDateTimeFormat() {
    return `${this.dateFormats.display.dateInput} hh:mm:ss A`;
  }

  /** Returns the seconds since Unix Epoch (UTC-based)
   Example: 03/07/22 12:47:24 PM -> 1646653644 or 2022-03-07T12:47:24 -> 1646657244
  */
  public dateStringToTicks = (dateString: string) => {
    if (dateString.includes('T') && dateString.endsWith('Z')) { // if date is in UTC format 2018-03-01T00:00:00Z
      return moment(dateString).unix();
    } else {
      return moment(dateString, this.getDateTimeFormat()).unix();
    }
  }

  /**
   * Converts Utc string to ticks
   */
  public dateUtcStringToTicks = (dateString: string) => {
    return moment.utc(dateString).unix(); // convert date to number
  }

  /**
   * Returns date part of datetime in ISO format
    2021-06-17T10:30:00.000 -> 2021-06-17
   */
  public dateReturnWithoutTime = (dateString: string) => {
    return moment(dateString).format("YYYY-MM-DD"); // convert date to number
  }

  /**
   * Returns user date format to UTC.
   * Ex: 2001/01/20 -> 2001-01-20T00:00:00Z,
   * Ex: 2001-01-20T00:00:00Z -> 2001-01-20T00:00:00Z,
   * Ex: null -> null,
   * Ex: '' -> null,
   */
  public dateStringToUtc(dateString: string): string {
    return dateString ? moment.utc(dateString, this.dateFormats.display.dateInput).format() : null;
  }

    /**
   * Returns user date format to UTC.
   * Ex: 2001-01-20T00:00:00Z -> 2001/01/20,
   * Ex: null -> null,
   * Ex: '' -> null,
   */
  public dateUtcStringToUserFormat(dateString: string,): string {
    return hasValue(dateString) ? moment.utc(dateString).format(this.dateFormats.display.dateInput) : null;
  }

  /***Returns UTC date format to user date format with time
   * 2021-06-17T10:30:00.000 -> 2021/06/17 10:30:00 AM
   * Ex: null -> '',
   * Ex: '' -> '', */
  public dateUtcStringToUserFormatWithTime(dateString: string): string {
    return hasValue(dateString) ? moment.utc(dateString).format(this.getDateTimeFormat()) : '';
  }

  /***Converts date to iso format:
   * 2021/06/17 -> 2021-06-17T00:00:00
   * 21/06/17 -> 2021-06-17T00:00:00.000
   * 21/06/17 10:30 AM -> 2021-06-17T10:30:00.000
   * without offset (Z) */
  public dateToIsoFormat(value: string, format: moment.MomentFormatSpecification, addDay: boolean): string {
    value = this.addZeroToHours(value);
    if (moment(value, format, true).isValid()) {
      if (addDay) {
        return moment(value, format, true).add(1, "day").subtract(1, "second").format(isoDateTimeFormat);
      } else {
        return moment(value, format, true).format(isoDateTimeFormat);
      }
    } else {
      return moment(value).format(isoDateTimeFormat);
    }
  }

  /** Add leading Zero to Hours */
  public addZeroToHours(value: string) {
    value = value.trim();
    let datetimeSplit = value.split(' ');
    if (datetimeSplit.length > 1) {
      value = datetimeSplit[0];
      if (datetimeSplit[1].length == '8:00:00'.length && datetimeSplit[1].split(':')[0].length == 1) {
        value += " 0" + datetimeSplit[1];
      } else {
        value += " " + datetimeSplit[1];
      }
      value += " " + datetimeSplit[2];
    }
    return value;
  }


  public addTimeToDate(value: string) {
    const defaultTime = '12:00:00 AM';
    const onlyDateProvied = value.length === this.dateFormats.display.dateInput.length;
    if(onlyDateProvied) {
      return `${value} ${defaultTime}`;
    }
    return value;
  }

  /**Converts date from ISO format to current User format (set in user page)
   * ex. * 2021-06-17T10:30:00 -> 2021/06/17 user format YYYY/MM/DD
  */
  public dateToUserDateFormat(value: moment.MomentInput, addTime: boolean = false): string {
    if (moment(value, isoDateTimeFormat, true).isValid()) {
      return moment(value, isoDateTimeFormat, true).format(addTime ? this.getDateTimeFormat() : this.dateFormats.display.dateInput);
    } else {
      return moment(value, null, true).format(isoDateTimeFormat);
    }
  }

  /**Converts date from ISO format to current User format without year
   * ex. * 2021-06-17T10:30:00 -> 06/17 user format YYYY/MM/DD
  */
     public dateToUserMonthAndDateFormat(value: moment.MomentInput): string {
      if (moment(value, isoDateTimeFormat, true).isValid()) {
        let separator;
        if(this.dateFormats.display.dateInput.includes('.')){
          separator = '.';
        }else if(this.dateFormats.display.dateInput.includes('-')){
          separator = '-';
        }else if(this.dateFormats.display.dateInput.includes('/')){
          separator = '/';
        }else {
          throw Error('Unsupported date separator');
        }

        let momentDate = moment(value, isoDateTimeFormat, true);
        let isMonthOnFirstPosition = this.dateFormats.display.dateInput.indexOf('M') < this.dateFormats.display.dateInput.indexOf('D');
        return isMonthOnFirstPosition ?
         `${momentDate.format("M")}${separator}${momentDate.format("D")}` :
         `${momentDate.format("D")}${separator}${momentDate.format("M")}`;
      } else {
        return moment(value, null, true).format(isoDateTimeFormat);
      }
    }
}
